import React from 'react';
import { useSelector } from 'react-redux';
import { ArrayHelpers, Forms, Loading, SystemText } from '@salesperformanceint/e4shared';
import Filters from '../_filters';
import Chart from './chart';
import GroupPicker from 'components/admin/forms/groupPicker';
import { apiGet } from 'services';
import WidgetBase, { groupSet, metricSet, widgetLoad, widgetLoadRecommendations } from '../_base';
import Navigation from '../_navigation';
import UserRecommendations from '../components/userRecommendations';

export const RadarCompetencies = ({ defaultProps, id, item, setItem, onDelete }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [recLoading, setRecLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [recommendationData, setRecommendationData] = React.useState({});
    const [areas, setAreas] = React.useState([]);
    const [area, setArea] = React.useState(0);
    const [flipped, setFlipped] = React.useState(true);

    const defaults = { group1: null, options: [] };

    React.useEffect(() => {
        const parsed = JSON.parse(item.data);
        setValues({ ...defaults, ...parsed });
    }, [item?.data]);

    React.useEffect(() => {
        load();
    }, [area]);

    const load = () => {
        apiGet(`competency-area`, user.guid, response => {
            setAreas(response.sort((a, b) => (a.name > b.name ? 1 : -1)));
            return response;
        }).then(response => {
            const qsParams = `?id=${availableAreas(response)[area]?.id}`;
            widgetLoad(
                item.id,
                user.guid,
                validator,
                setLoading,
                setNotConfigured,
                setData,
                () => {
                    widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData, qsParams);
                },
                null,
                qsParams
            );
        });
    };

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;
        setValues(temp);
    };

    const height = defaultProps.height(item);

    const validator = suppressErrors => {
        const form = Object.keys(values).length ? values : JSON.parse(item.data);
        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Edit/Fields/Name/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = SystemText('Dashboard/Widgets/RadarCompetencies/Fields/Group/Error');
            validForm = false;
        }

        if (!ArrayHelpers.existsAndHasLength(form.options)) {
            errors.options = SystemText('Dashboard/Widgets/RadarCompetencies/Fields/Areas/Error');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const nav = dir => {
        let a = area;
        a += dir;
        a = a < 0 ? availableAreas().length - 1 : a;
        a = a >= availableAreas().length ? 0 : a;
        setArea(a);
    };

    const availableAreas = React.useCallback(
        override => {
            const parsed = JSON.parse(item.data);
            return (override || areas)?.filter(a => parsed.options?.some(x => x === a.id));
        },
        [item.data, areas]
    );

    const chart = (
        <div
            className={`chart-container ${flipped ? 'flipped' : ''}`}
            id={`chart-${item.id}`}
            style={{ height: `${height}px` }}
            onClick={() => setFlipped(!flipped)}
        >
            <div className="chart" style={{ height: `${height}px` }}>
                <Chart data={data} areas={availableAreas()} item={item} id={`chart-${item.id}`} />
            </div>
        </div>
    );

    const recommendations = recLoading ? (
        <Loading />
    ) : recommendationData?.nodata ? (
        <div>{SystemText('Dashboard/Widgets/RadarCompetencies/Recommends/NoData')}</div>
    ) : recommendationData?.competency ? (
        <div>
            <UserRecommendations
                text={SystemText('Dashboard/Widgets/RadarCompetencies/Recommends/Behind', recommendationData?.competency)}
                users={recommendationData.worst}
            />
        </div>
    ) : (
        <div>
            <p>{SystemText('Dashboard/Widgets/RadarCompetencies/Recommends/TheBest')}</p>
        </div>
    );

    const filters = (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <GroupPicker
                help={SystemText('Dashboard/Widgets/RadarCompetencies/Fields/Group/Help')}
                mandatory
                allowMultiple
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                showUserOption={defaultProps.company}
                controlId={id}
            />
            <Forms.Select
                label={SystemText('Dashboard/Widgets/RadarCompetencies/Fields/Areas/Title')}
                placeholder={SystemText('Dashboard/Widgets/RadarCompetencies/Fields/Areas/Placeholder')}
                help={SystemText('Dashboard/Widgets/RadarCompetencies/Fields/Areas/Help')}
                data={areas}
                allowMultiple
                id={`radar-competencies-area-ids-${id}`}
                value={values.options}
                error={errors.options}
                onChange={value => updateProperty(value, 'options')}
            />
        </Filters>
    );

    const navigation =
        availableAreas()?.length > 1 ? <Navigation nav={nav} label={availableAreas()[area]?.name} id={id} /> : null;

    return (
        <WidgetBase
            filters={filters}
            navigation={navigation}
            chart={chart}
            height={height}
            item={item}
            recommendations={recommendations}
            id={id}
            help={null}
            hideConfig={defaultProps.company && !user.administrator}
            extra={null}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default RadarCompetencies;
